/**
 * Users Service
 * 
 * Handles all user-related API calls.
 */

import api from '../utils/api';

/**
 * Get all users
 * @param {Object} params - Query parameters (skip, limit, role, search, etc.)
 * @returns {Promise} - Get users response
 */
export const getUsers = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.role) queryParams.role = params.role;
    if (params.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/api/users${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Users retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve users',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get users error:', error);
    
    let errorMessage = 'Failed to retrieve users. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get a single user by ID
 * @param {string} userId - User ID
 * @returns {Promise} - Get user response
 */
export const getUser = async (userId) => {
  try {
    const response = await api.get(`/v1/api/users/${userId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve user',
      data: null,
    };
  } catch (error) {
    console.error('Get user error:', error);
    
    let errorMessage = 'Failed to retrieve user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update a user (full update using PUT)
 * @param {string} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise} - Update user response
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/v1/api/users/${userId}`, userData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update user',
      data: null,
    };
  } catch (error) {
    console.error('Update user error:', error);
    
    let errorMessage = 'Failed to update user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a user (PATCH)
 * @param {string} userId - User ID
 * @param {Object} userData - Fields to update (only include fields to update)
 * @returns {Promise} - Update user response
 */
export const updateUserPartial = async (userId, userData) => {
  try {
    const response = await api.patch(`/v1/api/users/${userId}`, userData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update user',
      data: null,
    };
  } catch (error) {
    console.error('Update user partial error:', error);
    
    let errorMessage = 'Failed to update user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a user
 * @param {string} userId - User ID
 * @returns {Promise} - Delete user response
 */
export const deleteUser = async (userId) => {
  try {
    const response = await api.delete(`/v1/api/users/${userId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'User deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete user',
    };
  } catch (error) {
    console.error('Delete user error:', error);
    
    let errorMessage = 'Failed to delete user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Activate a user
 * @param {string} userId - User ID
 * @returns {Promise} - Activate user response
 */
export const activateUser = async (userId) => {
  try {
    const response = await api.post(`/v1/api/users/${userId}/activate`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User activated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to activate user',
      data: null,
    };
  } catch (error) {
    console.error('Activate user error:', error);
    
    let errorMessage = 'Failed to activate user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Deactivate a user
 * @param {string} userId - User ID
 * @returns {Promise} - Deactivate user response
 */
export const deactivateUser = async (userId) => {
  try {
    const response = await api.post(`/v1/api/users/${userId}/deactivate`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'User deactivated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to deactivate user',
      data: null,
    };
  } catch (error) {
    console.error('Deactivate user error:', error);
    
    let errorMessage = 'Failed to deactivate user. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const usersService = {
  getUsers,
  getUser,
  updateUser,
  updateUserPartial,
  deleteUser,
  activateUser,
  deactivateUser,
};
export default usersService;

